/**
 * The reasons a detail panel shows no terminal, by name. A leaf, re-exported by `terminal-watch.ts`:
 * the launch entry reads a watch's refusal reason (`TerminalWatchApi`, for the browser's watch
 * reporter) and none of the watch's rules (T-0770, the entry budget).
 */

/** The reasons a detail panel shows no terminal. Ordered as `terminalWatchAvailability` asks them. */
export const TERMINAL_UNAVAILABLE_REASONS = [
  'session-over',
  // ⭐ T-0631: a daemon-held Codex session whose pane the bridge would not guess. Asked before
  // `no-terminal`, which is what such a session read as before the bridge said why.
  'several-clients',
  'several-sessions',
  'no-terminal',
  'not-in-tmux',
  'viewing-off',
  'no-tailscale',
  'no-certificate',
  'no-listener',
  'viewing-unknown',
  'not-running',
  // Learned only by trying: the ones below are set by the watch itself, never by the row.
  'unreachable',
  'terminal-unknown',
  'machine-busy',
  'pane-gone',
  // F-0683 slice 5: this page holds no device key to prove itself to the machine's listener with
  // (the listener's `device-proof-missing` reads the same), or the listener's challenge was not one.
  // A proof the listener refuses (`device-proof-invalid`) is `refused`.
  'device-key-missing',
  'challenge-invalid',
  // I-2215 step 3 (RLY-136): the machine takes only a sealed connection, which this page did not open.
  'seal-required',
  // OSP-23, OSP-10c: the desktop's own panel, whose host is this machine's bridge. A bridge from
  // before the cut-over hosts nothing, and with no bridge running nothing is served.
  'bridge-stale',
  'bridge-not-running',
  // OSP-71 (I-2367): the relay runs and the host beside it, which serves the panel, does not answer.
  'host-not-serving',
  // I-2585: the machine's redeem of the ticket never went out, because it could not reach FEATit;
  // nothing was spent and nothing opened. The machine ends the watch `deployment-unreachable`, the
  // start's word; read here under its own name, apart from MD-31's `deployment-unreachable` below,
  // where it is this device that got no ticket.
  'machine-cannot-reach-featit',
  // T-0859 (SCH-5n): the machine's redeem of the ticket was sent and never answered, so it showed
  // nothing; a new ticket is asked for.
  'outcome-unknown',
  // F-0720 (MD-31): the deployment gave no ticket, so the machine was never asked. Said in the words of
  // the connection's stage 2 (`machines.connection.failures`), never as the machine's refusal: a sign-in
  // it did not take (401), a refusal of its own (another 4xx), a failure (5xx), or no answer at all.
  'ticket-signed-out',
  'deployment-refused',
  'deployment-error',
  'deployment-unreachable',
  'refused',
] as const;
export type TerminalUnavailableReasonType = (typeof TERMINAL_UNAVAILABLE_REASONS)[number];

export function isTerminalUnavailableReason(
  value: unknown,
): value is TerminalUnavailableReasonType {
  return (
    typeof value === 'string' && (TERMINAL_UNAVAILABLE_REASONS as readonly string[]).includes(value)
  );
}
